/**
 * Supabase Client Singleton for Browser
 * 
 * This module provides a centralized Supabase client for all client-side components.
 * Using a singleton ensures consistent session management and storage configuration
 * across the entire application, which is especially important for PWA support.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ''

// Debug mode for session tracking (enable in development or when debugging PWA issues)
const DEBUG_SESSION = process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_DEBUG_AUTH === 'true'

function logDebug(message: string, data?: unknown) {
  if (DEBUG_SESSION && typeof window !== 'undefined') {
    const timestamp = new Date().toISOString()
    console.log(`[Supabase Auth ${timestamp}] ${message}`, data ?? '')
  }
}

// Singleton instance
let supabaseInstance: SupabaseClient | null = null

/**
 * Get the Supabase client singleton
 * Creates the client on first call, returns existing instance on subsequent calls
 */
export function getSupabaseClient(): SupabaseClient {
  if (supabaseInstance) {
    return supabaseInstance
  }

  if (!supabaseUrl || !supabaseAnonKey) {
    logDebug('⚠️ Supabase credentials not configured')
    throw new Error('Supabase URL and Anon Key must be configured')
  }

  logDebug('🔧 Creating Supabase client singleton', {
    url: supabaseUrl.substring(0, 30) + '...',
    hasAnonKey: !!supabaseAnonKey
  })

  supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
      flowType: 'pkce',
      // Use a consistent storage key for PWA
      storageKey: 'papanoel-auth-token',
      // Ensure we're using localStorage (default, but explicit for clarity)
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    }
  })

  // Set up auth state change listener for debugging
  if (DEBUG_SESSION && typeof window !== 'undefined') {
    supabaseInstance.auth.onAuthStateChange((event, session) => {
      logDebug(`🔄 Auth state changed: ${event}`, {
        hasSession: !!session,
        userId: session?.user?.id?.substring(0, 8) + '...',
        expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null
      })
    })

    // Log initial session state
    supabaseInstance.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        logDebug('❌ Error getting initial session', error.message)
      } else if (session) {
        logDebug('✅ Initial session found', {
          userId: session.user?.id?.substring(0, 8) + '...',
          email: session.user?.email,
          expiresAt: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : null
        })
      } else {
        logDebug('ℹ️ No initial session found')
      }
    })

    // Check localStorage for stored session
    try {
      const storedSession = window.localStorage.getItem('papanoel-auth-token')
      logDebug('💾 Stored session in localStorage', {
        exists: !!storedSession,
        size: storedSession?.length ?? 0
      })
    } catch (e) {
      logDebug('⚠️ Cannot access localStorage', e)
    }
  }

  return supabaseInstance
}

/**
 * For testing purposes - reset the singleton
 * Should only be used in tests
 */
export function resetSupabaseClient() {
  supabaseInstance = null
}
